import { useState } from 'react';

/**
 * Client-side pagination over an in-memory list. Keeps the current page, clamps
 * it when the list shrinks, and hands back the visible slice — pair with the
 * <Pagination> control below. Remount (e.g. a `key` on the list container) to
 * reset back to the first page.
 */
export function usePagination<T>(items: T[], pageSize = 25) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const clamped = Math.min(page, pageCount - 1);
  const start = clamped * pageSize;
  const pageItems = items.slice(start, start + pageSize);
  return {
    page: clamped,
    pageCount,
    pageItems,
    start,
    shown: pageItems.length,
    total: items.length,
    setPage,
    next: () => setPage((p) => Math.min(pageCount - 1, p + 1)),
    prev: () => setPage((p) => Math.max(0, p - 1)),
  };
}

/** "Showing 1–25 of 132" plus Prev/Next — hidden entirely for empty lists. */
export function Pagination({
  page,
  pageCount,
  total,
  start,
  shown,
  onPrev,
  onNext,
}: {
  page: number;
  pageCount: number;
  total: number;
  start: number;
  shown: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  if (total === 0) return null;
  const btn =
    'rounded-sm border border-border px-2 py-0.5 text-muted transition-colors hover:text-bone disabled:cursor-default disabled:opacity-40 disabled:hover:text-muted';
  return (
    <div className="flex items-center justify-between pt-2 text-[0.7rem] text-muted">
      <span>
        Showing {start + 1}–{start + shown} of {total}
      </span>
      {pageCount > 1 && (
        <div className="flex items-center gap-1.5">
          <button onClick={onPrev} disabled={page === 0} className={btn}>
            ← Prev
          </button>
          <span className="tabular-nums">
            {page + 1}/{pageCount}
          </span>
          <button onClick={onNext} disabled={page >= pageCount - 1} className={btn}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
